import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import AUSpinnerComponent from '@aarhus-university/au-lib-react-components/src/components/AUSpinnerComponent';
import AUReceiptComponent from '@aarhus-university/au-lib-react-components/src/components/AUReceiptComponent';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import { useTranslationsContext } from '../../lib/translation';
import { useIsTokenValidQuery, usePutPasswordMutation } from '../../services/api';
import { useAUPassword, useAUPasswordValidation } from '../../lib/hooks';
import { validatePassword } from '../../lib/validation';
import NewPasswordComponent from '../common/NewPasswordComponent';
import PasswordRequirementsComponent from '../common/PasswordRequirementsComponent';
import ReceiptNoticeComponent from '../common/ReceiptNoticeComponent';
import { RootState } from '../../reducers';
import {
  isPwned,
  setMessages as setMessagesConnect,
} from '../../reducers/password';

type Props = {
  messages: IMessage[],
  setMessages: (messages: IMessage[]) => void,
  token: string | null,
}

const SetPasswordComponent: FC<Props> = ({
  messages,
  setMessages,
  token,
}) => {
  const lang = useLangContext();
  const [validating, setValidating] = useState<boolean>(false);
  const translations = useTranslationsContext();

  const [putPassword, response] = usePutPasswordMutation();
  const { data: validToken, isLoading: isLoadingToken } = useIsTokenValidQuery(token || '', {
    skip: !token,
  });
  const {
    isUninitialized,
    isSuccess,
    isLoading,
    isError,
    error: serverError,
  } = response;

  const saving = !isUninitialized && isLoading;
  const saved = !isUninitialized && isSuccess;
  const error = !isUninitialized && isError;

  const [
    ,
    newPassword,
    confirmPassword, ,
    setNewPassword,
    setConfirmPassword,
  ] = useAUPassword(
    translations,
    error,
    serverError,
    setMessages,
    setValidating,
  );

  const [
    newPasswordValidated,
    confirmPasswordValidated, ,
    newPasswordValidationMessages,
    confirmPasswordValidationMessages,
    setNewPasswordValidated,
    setConfirmPasswordValidated, ,
    setNewPasswordValidationMessages,
    setConfirmPasswordValidationMessages,
  ] = useAUPasswordValidation();

  const handleSubmit = (): void => {
    setValidating(true);
    validatePassword(
      translations,
      0,
      newPassword,
      confirmPassword,
      false,
      true,
      isPwned,
      translations.change_password.new.validation.insecure,
    ).then(
      (v: IValidation) => {
        setNewPasswordValidated(v.newPasswordValid);
        setNewPasswordValidationMessages(v.newPasswordMessages);
        setConfirmPasswordValidated(v.confirmPasswordValid);
        setConfirmPasswordValidationMessages(v.confirmPasswordMessages);
        if (v.newPasswordValid && v.confirmPasswordValid) {
          putPassword({
            token: token as string,
            password: newPassword,
          });
        } else {
          setValidating(false);
        }
      },
    );
  };

  if ((!isLoadingToken && !validToken) || !token) {
    return (
      <div className="page__content__block">
        <h1>{translations.forgot.expiration.header}</h1>
        <p>{translations.forgot.expiration.text}</p>
        <a className="button" href={`/${lang}/password/forgot`}>{translations.forgot.expiration.link}</a>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="page__content__block">
        <AUReceiptComponent
          receiptText={translations.forgot.receipt.success}
          helpText={(
            <ReceiptNoticeComponent />
          )}
          showGoTo={false}
        />
      </div>
    );
  }

  return (
    <AUSpinnerComponent loaded={!isLoadingToken}>
      <div className="page__content__block">
        <h1>{translations.change_password.title}</h1>
        <p>
          {translations.change_password.intro}
        </p>
        <form
          className="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="row row--wrap-reverse row--equal-height-items">
            <NewPasswordComponent
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              setNewPassword={setNewPassword}
              setConfirmPassword={setConfirmPassword}
              newPasswordValidated={newPasswordValidated}
              confirmPasswordValidated={confirmPasswordValidated}
              setNewPasswordValidated={setNewPasswordValidated}
              setConfirmPasswordValidated={setConfirmPasswordValidated}
              newPasswordValidationMessages={newPasswordValidationMessages}
              confirmPasswordValidationMessages={confirmPasswordValidationMessages}
              setNewPasswordValidationMessages={setNewPasswordValidationMessages}
              setConfirmPasswordValidationMessages={setConfirmPasswordValidationMessages}
              errorMessages={messages.filter((x) => x.status === 'PreviouslyUsedPassword' || x.status === 'GenericError' || x.status === 'TokenAlreadyUsed')}
              uiBusy={validating || saving}
              isWithinAUStepComponent={false}
            />
            <PasswordRequirementsComponent />
          </div>
        </form>
      </div>
    </AUSpinnerComponent>
  );
};

SetPasswordComponent.displayName = 'SetPasswordComponent';
export default connect(
  (state: RootState) => state.password,
  {
    setMessages: setMessagesConnect,
  },
)(SetPasswordComponent);
